// When to talk about offline storage. Synced data is safe on the server; only
// changes still on this device can be lost if the browser clears its storage,
// which it may do (under storage pressure) unless the storage is protected.

/** Unsynced changes older than this, on unprotected storage, deserve a real warning. */
export const UNSYNCED_WARN_MINUTES = 60

/**
 * "protected": nothing to say. "unprotected": a quiet ⓘ and a one-time tip.
 * "at-risk": unprotected and changes have waited too long to sync: a banner.
 * null: not known yet.
 */
export type StorageAdvice = 'protected' | 'unprotected' | 'at-risk'

export function storageAdvice(
  storage: { persisted: boolean } | null | undefined,
  oldestUnsyncedAt: string | null,
  now: Date,
): StorageAdvice | null {
  if (!storage) return null
  if (storage.persisted) return 'protected'
  if (
    oldestUnsyncedAt &&
    now.getTime() - new Date(oldestUnsyncedAt).getTime() >= UNSYNCED_WARN_MINUTES * 60_000
  )
    return 'at-risk'
  return 'unprotected'
}

/** 1536 → "1.5 KB", 5_242_880 → "5 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  const rounded = value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${units[unit]}`
}
